import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Container, Form, Spinner, Toast, ToastContainer } from "react-bootstrap";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { register } from "../../actions/authActions";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptySignupErrors = {
  username: "",
  email: "",
  phone: "",
  address: "",
  password: "",
  confirm_password: "",
  role_id: "",
};

const normalizeSignupFieldErrors = (fieldErrors = {}) => ({
  ...emptySignupErrors,
  username: fieldErrors.username || "",
  email: fieldErrors.email || "",
  phone: fieldErrors.phone || "",
  address: fieldErrors.address || "",
  password: fieldErrors.password || "",
  role_id: fieldErrors.role_id || fieldErrors.role_name || fieldErrors.role || "",
});

const SignupAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    confirm_password: "",
    role_id: "admin",
  });

  const [errors, setErrors] = useState(emptySignupErrors);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");

  const { username, email, phone, address, password, confirm_password, role_id } = formData;

  const onChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setApiError("");
    setApiSuccess("");
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validate = () => {
    const next = { ...emptySignupErrors };
    if (!username.trim()) next.username = "Username is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!emailRegex.test(email.trim())) next.email = "Enter a valid email.";
    if (!address.trim()) next.address = "Address is required.";
    if (!phone.toString().trim()) next.phone = "Phone is required.";
    if (!role_id) next.role_id = "Role is required.";
    if (!password) next.password = "Password is required.";
    else if (password.length < 6) next.password = "Min 6 characters.";
    if (!confirm_password) next.confirm_password = "Confirm your password.";
    else if (confirm_password !== password) next.confirm_password = "Passwords do not match.";
    setErrors(next);
    return Object.values(next).every((value) => !value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    setApiError("");
    setApiSuccess("");

    try {
      const result = await dispatch(
        register({
          username: username.trim(),
          email: email.trim(),
          phone,
          address: address.trim(),
          password,
          role_name: role_id,
        })
      );

      if (result?.ok) {
        setApiSuccess("Account created successfully. Redirecting to login…");
        navigate("/login", { replace: true, state: { signupSuccess: true } });
      } else {
        setErrors(normalizeSignupFieldErrors(result?.fieldErrors));
        setApiError(result?.error || "Unable to sign up. Please check the highlighted fields and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />
      <Container fluid className="d-flex justify-content-center">
        <Form className="form-container p-4" onSubmit={onSubmit} noValidate>
          <h3>Signup</h3>

          <ToastContainer position="top-center" className="p-3" style={{ zIndex: 1060 }}>
            <Toast bg="danger" show={!!apiError} onClose={() => setApiError("")}>
              <Toast.Header>
                <strong className="me-auto">Signup error</strong>
              </Toast.Header>
              <Toast.Body className="text-white">{apiError}</Toast.Body>
            </Toast>
            <Toast bg="success" show={!!apiSuccess} onClose={() => setApiSuccess("")} delay={4500} autohide>
              <Toast.Header>
                <strong className="me-auto">Signup successful</strong>
              </Toast.Header>
              <Toast.Body className="text-white">{apiSuccess}</Toast.Body>
            </Toast>
          </ToastContainer>

          {apiError && (
            <div className="text-danger small mt-2" role="status" aria-live="polite">
              {apiError}
            </div>
          )}

          <Form.Group className="form-column">
            <Form.Label>Username</Form.Label>
            <Form.Control type="text" name="username" value={username} onChange={onChange} isInvalid={!!errors.username} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" name="email" value={email} onChange={onChange} isInvalid={!!errors.email} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Address</Form.Label>
            <Form.Control type="text" name="address" value={address} onChange={onChange} isInvalid={!!errors.address} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Phone</Form.Label>
            <Form.Control type="number" name="phone" value={phone} onChange={onChange} isInvalid={!!errors.phone} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Password</Form.Label>
            <Form.Control type="password" name="password" value={password} onChange={onChange} isInvalid={!!errors.password} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Confirm Password</Form.Label>
            <Form.Control type="password" name="confirm_password" value={confirm_password} onChange={onChange} isInvalid={!!errors.confirm_password} required disabled={submitting} />
            <Form.Control.Feedback type="invalid">{errors.confirm_password}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="form-column">
            <Form.Label>Role</Form.Label>
            <Form.Control as="select" name="role_id" value={role_id} onChange={onChange} isInvalid={!!errors.role_id} required disabled={submitting}>
              <option value="admin">Admin</option>
              <option value="fleetrole">Distributors/Retailers</option>
              <option value="companyrole">Fleets/Corporate Clients</option>
              <option value="thirdprole">3rd Party Suppliers</option>
              <option value="workshoprole">Workshops/Garages</option>
              <option value="b2crole">B2C Customers</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.role_id}</Form.Control.Feedback>
          </Form.Group>
          <Button variant="primary" type="submit" className="login-button d-inline-flex align-items-center gap-2" disabled={submitting} aria-busy={submitting}>
            {submitting && <Spinner animation="border" size="sm" />}
            {submitting ? "Submitting…" : "Signup"}
          </Button>
          <div className="mt-3">
            Do you have an account?
            <Link to="/login">
              <span> Login here</span>
            </Link>
          </div>
        </Form>
      </Container>
      <Footer />
    </div>
  );
};

export default SignupAdmin;
